import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  invalidateChatTokenForMutualFollow,
  invalidateSocialGraph,
} from "Api/Mutations/Social/invalidateSocialGraph";
import { userInfoKeys } from "Auth/Api/Queries/useUserInfoQuery";
import {
  putSocialProfileVisibility,
  type SetProfileVisibilityRequest,
  type SetProfileVisibilityResult,
  type UserInfoQueryResult,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

/**
 * Sets the active persona's public/private profile mode. Optimistic: the user-info cache flips
 * immediately and rolls back on failure; a settle-time invalidation reconciles with the server.
 * Going public approves every pending follow request server-side, so the social graph is
 * reconciled too — and the pairs that made mutual get their DMs promoted only by the chat token
 * sweep, so that one case refetches the token.
 */
export const useSetProfileVisibilityMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation<
    SetProfileVisibilityResult,
    Error,
    SetProfileVisibilityRequest,
    { previousUserInfo: UserInfoQueryResult | undefined }
  >({
    mutationFn: processResponseWithApiProblemDetails(
      (data: SetProfileVisibilityRequest) => putSocialProfileVisibility(data),
    ),
    onMutate: async ({ isPublic }) => {
      await queryClient.cancelQueries({ queryKey: userInfoKeys.all });

      const previousUserInfo = queryClient.getQueryData<UserInfoQueryResult>(
        userInfoKeys.all,
      );

      queryClient.setQueryData<UserInfoQueryResult>(userInfoKeys.all, (old) =>
        old ? { ...old, isProfilePublic: isPublic } : old,
      );

      return { previousUserInfo };
    },
    onSuccess: (_result, { isPublic }) => {
      if (isPublic) {
        invalidateChatTokenForMutualFollow(queryClient);
      }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUserInfo) {
        queryClient.setQueryData(userInfoKeys.all, context.previousUserInfo);
      }
      // ZS-1643: the global MutationCache only reports to Sentry — a user-initiated
      // save toasts its own message, like the other optimistic user-info toggles.
      toast.error(t(Resources.Common.SaveError));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userInfoKeys.all });
      invalidateSocialGraph(queryClient);
    },
  });
};
